import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useGuestAppointments } from "@/providers/GuestAppointments";
import type { AppointmentRequest, Offering } from "@/lib/slotting/types";
import type { Lane, Treatment } from "@/lib/slotting/types";
import {
  getGuestRequestsAllOfferings,
  getOfferings,
  getLanes,
  getTreatments,
  getGuestsByIds,
} from "@/lib/slotting/api";

/**
 * Window event fired after a create/edit flow saved an appointment or request
 * for a guest. Stacked guest-detail popups mount separate provider instances,
 * so a direct refetch would only reach the popup that owned the flow — the
 * broadcast lets every mounted tab for that guest refresh itself.
 */
export const GUEST_SLOTTING_CHANGED_EVENT = "guestSlottingChanged";
export type GuestSlottingChangedDetail = {
  guestId: string;
  kind: "appointment" | "request";
};

/**
 * Data for the merged appointments+requests tab of the guest-detail popup.
 *
 * Owns the requests fetch (the appointments half stays on the shared
 * GuestAppointmentsProvider) and builds the offering/lane/treatment/guest-name
 * maps ONCE for both halves — the two old lists each ran their own fetch
 * pyramid for the same data.
 */
export function useGuestSlottingTabData(
  guestId: string,
  firstName?: string,
  lastName?: string,
) {
  const params = useParams();
  const eventId = params.eventId as string;

  const {
    allAppointmentsState,
    fetchAppointments,
    loading: appointmentsLoading,
    errorMessage: appointmentsError,
  } = useGuestAppointments();
  const appointments = allAppointmentsState.get(guestId);

  const [requests, setRequests] = useState<AppointmentRequest[] | null>(null);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState(false);

  const [offeringMap, setOfferingMap] = useState<Map<string, string>>(new Map());
  // Full offering objects — the appointment-details popup needs statusDotRules,
  // not just the name. Same single getOfferings fetch feeds both maps.
  const [offeringsById, setOfferingsById] = useState<Map<string, Offering>>(new Map());
  const [treatmentMap, setTreatmentMap] = useState<Map<string, string>>(new Map());
  const [laneMap, setLaneMap] = useState<Map<string, string>>(new Map());
  const [guestNameMap, setGuestNameMap] = useState<Map<string, string>>(new Map());

  const loadRequests = useCallback(async () => {
    if (!guestId || !eventId) return;
    setRequestsLoading(true);
    setRequestsError(false);
    try {
      const result = await getGuestRequestsAllOfferings(eventId, guestId);
      setRequests(result ?? []);
    } catch {
      setRequestsError(true);
      setRequests((prev) => prev ?? []);
    } finally {
      setRequestsLoading(false);
    }
  }, [eventId, guestId]);

  // Initial load — once per mount (like the old lists' executed refs).
  const initialFetched = useRef(false);
  useEffect(() => {
    if (initialFetched.current || !guestId || !eventId) return;
    initialFetched.current = true;
    fetchAppointments(guestId);
    loadRequests();
  }, [guestId, eventId, fetchAppointments, loadRequests]);

  // Cross-popup sync: refetch when ANY popup layer saves slotting data for
  // this guest (see GUEST_SLOTTING_CHANGED_EVENT). The provider supports
  // force-refetch; the requests half re-runs its own fetch (no provider).
  useEffect(() => {
    const onChanged = (e: Event) => {
      const detail = (e as CustomEvent<GuestSlottingChangedDetail>).detail;
      if (detail?.guestId !== guestId) return;
      if (detail.kind === "appointment") fetchAppointments(guestId, true);
      else loadRequests();
    };
    window.addEventListener(GUEST_SLOTTING_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(GUEST_SLOTTING_CHANGED_EVENT, onChanged);
  }, [guestId, fetchAppointments, loadRequests]);

  // Name maps for both halves, fetched once per offering. Marked BEFORE the
  // await so a re-render during flight can't double-fetch; a refetch that
  // surfaces a NEW offering only loads that offering's lanes/treatments.
  const offeringsFetched = useRef(false);
  const mappedOfferingIds = useRef(new Set<string>());
  useEffect(() => {
    if (!eventId) return;
    const rows = [...(appointments ?? []), ...(requests ?? [])];
    if (rows.length === 0) return;

    const missing = [...new Set(rows.map((r) => r.offeringId))].filter(
      (id) => id && !mappedOfferingIds.current.has(id),
    );
    if (missing.length === 0) return;
    missing.forEach((id) => mappedOfferingIds.current.add(id));

    (async () => {
      if (!offeringsFetched.current) {
        offeringsFetched.current = true;
        const offerings = await getOfferings(eventId).catch(() => []);
        setOfferingMap((prev) => {
          const next = new Map(prev);
          offerings.forEach((o) => next.set(o.offeringId, o.name));
          return next;
        });
        setOfferingsById((prev) => {
          const next = new Map(prev);
          offerings.forEach((o) => next.set(o.offeringId, o));
          return next;
        });
      }
      const [lanesByOffering, treatmentsByOffering] = await Promise.all([
        Promise.all(
          missing.map((id) => getLanes(eventId, id).catch(() => [] as Lane[])),
        ),
        Promise.all(
          missing.map((id) => getTreatments(eventId, id).catch(() => [] as Treatment[])),
        ),
      ]);
      setLaneMap((prev) => {
        const next = new Map(prev);
        lanesByOffering.flat().forEach((l) => next.set(l.laneId, l.name));
        return next;
      });
      setTreatmentMap((prev) => {
        const next = new Map(prev);
        treatmentsByOffering.flat().forEach((tr) => next.set(tr.treatmentId, tr.name));
        return next;
      });
    })();
  }, [eventId, appointments, requests]);

  // Guest display names for the appointment-details popup (companions + the
  // viewed guest). Seed the viewed guest from props, batch-fetch the rest.
  const resolvedGuestIds = useRef(new Set<string>());
  useEffect(() => {
    if (!eventId || !appointments || appointments.length === 0) return;

    if (!resolvedGuestIds.current.has(guestId)) {
      resolvedGuestIds.current.add(guestId);
      const viewedName = [firstName, lastName].filter(Boolean).join(" ");
      if (viewedName) {
        setGuestNameMap((prev) => new Map(prev).set(guestId, viewedName));
      }
    }

    const missing = [
      ...new Set(appointments.flatMap((a) => a.guests.map((g) => g.guestId))),
    ].filter((id) => id && !resolvedGuestIds.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => resolvedGuestIds.current.add(id));

    (async () => {
      const guests = await getGuestsByIds(eventId, missing).catch(() => []);
      if (guests.length === 0) return;
      setGuestNameMap((prev) => {
        const next = new Map(prev);
        guests.forEach((g) => {
          const name =
            [g.firstName, g.lastName].filter(Boolean).join(" ") || g.email;
          if (g.guestId && name) next.set(g.guestId, name);
        });
        return next;
      });
    })();
  }, [eventId, appointments, guestId, firstName, lastName]);

  const openRequests = (requests ?? [])
    .filter((r) => !r.archived)
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));

  return {
    eventId,
    appointments,
    openRequests,
    // Initial spinner until BOTH halves have landed at least once.
    loading:
      appointmentsLoading || requestsLoading || appointments === undefined || requests === null,
    appointmentsError,
    requestsError,
    offeringMap,
    offeringsById,
    treatmentMap,
    laneMap,
    guestNameMap,
  };
}
